import {
  IOConfig,
  IO_CONFIG_UNKNOWN_ID,
  LU_PROTOCOL_LABEL,
} from '@/types/controller'

export interface IOConfigOption {
  id: number
  // What the user picks. Disambiguated when two configurations share a
  // description, so it can always be resolved back to a single id.
  label: string
  luTypeProtocolMask: number
  ioTypeMask: number
}

/**
 * A decoder's line unit protocol.
 *
 * The line unit is the authoritative source, but not every decoder has one —
 * on Demo Controller D2 twenty-three of them do not. In that case the
 * configuration already assigned to the decoder gives the answer, since a
 * configuration only exists for one protocol. The wildcard "Unknown"
 * configuration says nothing, so it is ignored.
 */
export const resolveDecoderProtocol = (
  configs: IOConfig[] | undefined,
  protocolFromLineUnit: number | undefined,
  currentConfigId: number | undefined,
): number | undefined => {
  if (protocolFromLineUnit) return protocolFromLineUnit
  if (!configs?.length || !currentConfigId) return undefined

  const current = configs.find(
    (config) => Number(config.id) === Number(currentConfigId),
  )
  if (!current || current.id === IO_CONFIG_UNKNOWN_ID) return undefined

  const protocol = Number(current.luTypeProtocolMask)
  // A mask covering several protocols cannot identify one.
  return protocol in LU_PROTOCOL_LABEL ? protocol : undefined
}

/**
 * The configurations a decoder can actually take.
 *
 * A configuration applies to one decoder type (output / input / sdi-12) and one
 * line unit protocol (rkd / mk2 / mk3), so both have to match. The "Unknown"
 * wildcard is always offered.
 *
 * When the protocol cannot be resolved — some interfaces have no line unit
 * types — the protocol half of the filter is dropped rather than returning
 * nothing. That can surface several same-named configurations (there is one
 * "Default" per protocol), so those get their protocol appended.
 */
export const getIOConfigOptions = (
  configs: IOConfig[] | undefined,
  ioType: number | undefined,
  protocol: number | undefined,
): IOConfigOption[] => {
  if (!configs?.length || !ioType) return []

  const matches = configs.filter((config) => {
    if (config.id === IO_CONFIG_UNKNOWN_ID) return true
    if (Number(config.ioTypeMask) !== Number(ioType)) return false
    if (!protocol) return true
    return Number(config.luTypeProtocolMask) === Number(protocol)
  })

  const descriptionCounts = new Map<string, number>()
  matches.forEach((config) => {
    descriptionCounts.set(
      config.description,
      (descriptionCounts.get(config.description) ?? 0) + 1,
    )
  })

  return matches.map((config) => {
    const isAmbiguous = (descriptionCounts.get(config.description) ?? 0) > 1
    const protocolLabel = LU_PROTOCOL_LABEL[Number(config.luTypeProtocolMask)]

    return {
      id: config.id,
      label:
        isAmbiguous && protocolLabel
          ? `${config.description} (${protocolLabel})`
          : config.description,
      luTypeProtocolMask: config.luTypeProtocolMask,
      ioTypeMask: config.ioTypeMask,
    }
  })
}

/**
 * Resolves what the user picked back to a configuration id.
 *
 * Falls back to the id the row already had: writing 0 here is what silently
 * reset decoders to "Unknown".
 */
export const resolveIOConfigId = (
  options: IOConfigOption[],
  selectedLabel: string | undefined,
  currentId: number | undefined,
): number => {
  const match = options.find((option) => option.label === selectedLabel)
  if (match) return match.id

  // The stored value is shown undecorated, so also try the raw description.
  const byDescription = options.find(
    (option) => option.label.split(' (')[0] === selectedLabel,
  )
  if (byDescription) return byDescription.id

  return currentId ?? IO_CONFIG_UNKNOWN_ID
}
